import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Crown, Trophy, Medal, Award } from 'lucide-react'

const RankingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
    animation: backgroundAnimation 10s ease-in-out infinite alternate;
  }

  @keyframes backgroundAnimation {
    0% {
      transform: scale(1) rotate(0deg);
      opacity: 0.7;
    }
    100% {
      transform: scale(1.1) rotate(5deg);
      opacity: 0.9;
    }
  }
`

const RankingContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 500px;
  padding: 20px;
  text-align: center;
`

const Title = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: #FFD700;
  text-shadow: 
    2px 2px 4px rgba(0,0,0,0.5),
    0 0 20px rgba(255,215,0,0.3);
  margin-bottom: 20px;
  letter-spacing: 4px;
  text-transform: uppercase;
`

const GoalBanner = styled.div`
  background: linear-gradient(135deg, #00d4ff, #00a6d6);
  color: white;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 30px;
  box-shadow: 0 6px 20px rgba(0,212,255,0.4);
  letter-spacing: 2px;
`

const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
`

const RankingItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 15px 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
    border-color: rgba(255,255,255,0.4);
  }
`

const RankBadge = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 800;
  color: white;
  margin-right: 15px;
  position: relative;
  
  background: ${props => {
    switch(props.$rank) {
      case 1: return 'linear-gradient(135deg, #FFD700, #FFA500)'
      case 2: return 'linear-gradient(135deg, #C0C0C0, #A0A0A0)'
      case 3: return 'linear-gradient(135deg, #CD7F32, #B8860B)'
      case 4: return 'linear-gradient(135deg, #4ECDC4, #44A08D)'
      default: return 'linear-gradient(135deg, #667eea, #764ba2)'
    }
  }};

  box-shadow: ${props => {
    switch(props.$rank) {
      case 1: return '0 6px 20px rgba(255,215,0,0.4)'
      case 2: return '0 6px 20px rgba(192,192,192,0.4)'
      case 3: return '0 6px 20px rgba(205,127,50,0.4)'
      case 4: return '0 6px 20px rgba(78,205,196,0.4)'
      default: return '0 6px 20px rgba(102,126,234,0.4)'
    }
  }};

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${props => {
      switch(props.$rank) {
        case 1: return 'linear-gradient(135deg, #FFD700, #FFA500)'
        case 2: return 'linear-gradient(135deg, #C0C0C0, #A0A0A0)'
        case 3: return 'linear-gradient(135deg, #CD7F32, #B8860B)'
        case 4: return 'linear-gradient(135deg, #4ECDC4, #44A08D)'
        default: return 'linear-gradient(135deg, #667eea, #764ba2)'
      }
    }};
    border-radius: 50%;
    z-index: -1;
    opacity: 0.3;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`

const PlayerAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.$bgColor || 'linear-gradient(135deg, #667eea, #764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
  border: 3px solid rgba(255,255,255,0.3);
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
`

const PlayerName = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: white;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
`

const PlayerScore = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: white;
  background: rgba(255,255,255,0.2);
  padding: 8px 20px;
  border-radius: 25px;
  min-width: 120px;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.3);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.1);
`

const BottomMessage = styled.div`
  color: #FFD700;
  font-size: 16px;
  font-weight: 600;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  margin-top: 20px;
  opacity: 0.9;
`

const getRankIcon = (rank) => {
  switch(rank) {
    case 1: return <Crown size={28} />
    case 2: return <Trophy size={24} />
    case 3: return <Medal size={24} />
    case 4: return <Award size={24} />
    default: return rank
  }
}

const GameRankingBoard = ({ players = [], goal = 200000, onClose, onGoalAchieved }) => {
  // 기본 플레이어 데이터 (props가 없을 때)
  const defaultPlayers = [
    {
      id: 1,
      name: '이유준',
      score: 180000,
      avatar: '🏆',
      bgColor: 'linear-gradient(135deg, #FFD700, #FFA500)'
    },
    {
      id: 2,
      name: '박민지',
      score: 150500,
      avatar: '👑',
      bgColor: 'linear-gradient(135deg, #FF6B6B, #FF8E53)'
    },
    {
      id: 3,
      name: '최나',
      score: 130230,
      avatar: '🎮',
      bgColor: 'linear-gradient(135deg, #4ECDC4, #44A08D)'
    },
    {
      id: 4,
      name: '나현이',
      score: 110984,
      avatar: '🎯',
      bgColor: 'linear-gradient(135deg, #A8E6CF, #88D8A3)'
    }
  ]

  const displayPlayers = players.length > 0 ? players.slice(0, 4) : defaultPlayers

  // Goal 달성 시뮬레이션 (5초 후 자동으로 goal 달성)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onGoalAchieved) {
        onGoalAchieved()
      }
    }, 5000) // 5초 후 goal 달성

    return () => clearTimeout(timer)
  }, [onGoalAchieved])

  const handleContainerClick = () => {
    if (onClose) {
      onClose()
    }
    // 클릭으로 조기 종료 시에도 최종 랭킹으로 이동
    if (onGoalAchieved) {
      onGoalAchieved()
    }
  }

  return (
    <RankingContainer onClick={handleContainerClick}>
      <RankingContent onClick={(e) => e.stopPropagation()}>
        <Title>RANKING</Title>
        
        <GoalBanner>
          GOAL : {goal.toLocaleString()}
        </GoalBanner>

        <RankingList>
          {displayPlayers.map((player, index) => (
            <RankingItem key={player.id || index}>
              <RankBadge $rank={index + 1}>
                {getRankIcon(index + 1)}
              </RankBadge>
              
              <PlayerInfo>
                <PlayerAvatar $bgColor={player.bgColor}>
                  {player.avatar}
                </PlayerAvatar>
                <PlayerName>{player.name}</PlayerName>
              </PlayerInfo>
              
              <PlayerScore>
                {player.score.toLocaleString()}
              </PlayerScore>
            </RankingItem>
          ))}
        </RankingList>

        <BottomMessage>
          나머지 님이 5000점을 추가로 획득합니다!
        </BottomMessage>
      </RankingContent>
    </RankingContainer>
  )
}

export default GameRankingBoard